<template>
  <div class="invoice-history-page">
    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p>Loading invoice history...</p>
    </div>

    <div v-else>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Statement History – {{ leaseReference }}</span>
        <v-btn color="primary" small outlined @click="fetchInvoices" :loading="refreshing">
          <v-icon left small>mdi-refresh</v-icon> Refresh
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="invoices"
        :items-per-page="10"
        class="elevation-1"
        :loading="refreshing"
      >
        <template v-slot:item.datePaid="{ item }">
          {{ formatDate(item.datePaid) }}
        </template>
        <template v-slot:item.amount="{ item }">
          R {{ formatAmount(item.amountPaid) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small @click="viewInvoice(item)" class="mr-2" color="primary">mdi-eye</v-icon>
          <v-icon small @click="downloadInvoice(item)" class="mr-2" color="info">mdi-download</v-icon>
          <v-icon small @click="resendInvoice(item)" color="success">mdi-email</v-icon>
        </template>
        <template v-slot:no-data>
          <div class="text-center pa-4">
            <v-icon size="48" color="grey lighten-1">mdi-inbox</v-icon>
            <p class="mt-2">No invoices found for this lease.</p>
            <v-btn color="primary" text @click="$emit('generate-invoice')">Generate first invoice</v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- View Invoice Dialog -->
      <v-dialog v-model="showViewInvoiceDialog" max-width="800" scrollable>
        <v-card>
          <v-card-title class="headline">Invoice {{ selectedInvoice.reference }}</v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <div class="invoice-preview" v-html="invoiceHtml" v-if="invoiceHtml"></div>
            <div v-else class="text-center pa-8">
              <v-progress-circular indeterminate></v-progress-circular>
              <p class="mt-2">Loading preview...</p>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="downloadInvoice(selectedInvoice)" :disabled="!selectedInvoice.pdfBase64">
              <v-icon left>mdi-download</v-icon> Download PDF
            </v-btn>
            <v-btn color="primary" @click="resendInvoice(selectedInvoice)" :disabled="!selectedInvoice.pdfBase64">
              <v-icon left>mdi-email</v-icon> Resend
            </v-btn>
            <v-btn text @click="showViewInvoiceDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, query, where, orderBy, updateDoc, doc, getDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";

const db = getFirestore();

export default {
  props: {
    leaseReference: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      refreshing: false,
      showViewInvoiceDialog: false,
      selectedInvoice: {},
      invoiceHtml: null,
      invoices: [],
      headers: [
        { text: "Invoice Number", value: "reference", sortable: true },
        { text: "Date", value: "datePaid", sortable: true },
        { text: "Description", value: "description" },
        { text: "Total Amount", value: "amount", sortable: true, align: "end" },
        { text: "Status", value: "status", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
    };
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },

    /**
     * Safely parse any amount value into a formatted "0.00" string.
     * Handles: numbers, numeric strings, "R 150.00", "150,00", null, undefined.
     */
    parseAmountToNumber(raw) {
      if (raw === null || raw === undefined || raw === "") return 0;
      if (typeof raw === "number") return isNaN(raw) ? 0 : raw;

      // Strip everything except digits, minus, and separators
      let cleaned = String(raw).replace(/[^\d.,-]/g, "").trim();

      // Handle comma as decimal separator (e.g. "150,00" → "150.00")
      // If both , and . exist, assume , is thousands and . is decimal
      if (cleaned.includes(",") && cleaned.includes(".")) {
        cleaned = cleaned.replace(/,/g, "");
      } else if (cleaned.includes(",")) {
        cleaned = cleaned.replace(",", ".");
      }

      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    },

    formatAmount(raw) {
      const num = this.parseAmountToNumber(raw);
      return Math.abs(num).toFixed(2);
    },

    async fetchInvoices() {
      this.refreshing = true;
      try {
        const q = query(
          collection(db, "Invoices"),
          where("leaseReference", "==", this.leaseReference),
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        this.invoices = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          // Try multiple possible field names for the amount
          const rawAmount =
            data.amountPaid ??
            data.amount ??
            data.total ??
            data.amountDue ??
            data.totalAmount ??
            0;

          const numericAmount = this.parseAmountToNumber(rawAmount);
          const safeAmount = Math.abs(numericAmount).toFixed(2);

          return {
            id: docSnap.id,
            ...data,
            datePaid:
              data.datePaid ||
              (data.createdAt?.toDate?.()?.toISOString().split("T")[0]) ||
              "",
            amountPaid: safeAmount,
            status: data.status || "sent",
          };
        });
      } catch (error) {
        console.error("Error fetching invoices:", error);
        this.$toast?.error("Failed to load invoice history");
      } finally {
        this.refreshing = false;
        this.loading = false;
      }
    },

    viewInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.showViewInvoiceDialog = true;
      if (invoice.pdfBase64) {
        const pdfDataUrl = `data:application/pdf;base64,${invoice.pdfBase64}`;
        this.invoiceHtml = `<embed src="${pdfDataUrl}" width="100%" height="600px" type="application/pdf" />`;
      } else {
        this.invoiceHtml =
          "<div class='pa-4 text-center'>No PDF data available for this invoice.</div>";
      }
    },

    downloadInvoice(invoice) {
      if (invoice.pdfBase64) {
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${invoice.pdfBase64}`;
        link.download = `Invoice_${invoice.reference || "document"}.pdf`;
        link.click();
      } else {
        alert("PDF data not available for this invoice.");
      }
    },

    async resendInvoice(invoice) {
      if (!invoice.pdfBase64) {
        alert("Cannot resend: PDF data missing.");
        return;
      }

      // Fetch tenant email from the lease data
      let tenantEmail = "";
      let tenantName = "";
      try {
        const leaseQuery = query(
          collection(db, "Leases"),
          where("leaseReference", "==", this.leaseReference)
        );
        const leaseSnap = await getDocs(leaseQuery);
        if (!leaseSnap.empty) {
          const leaseData = leaseSnap.docs[0].data();
          const tenantId = leaseData.tenantId;
          if (tenantId) {
            const tenantDoc = await getDoc(doc(db, "tenants", tenantId));
            if (tenantDoc.exists()) {
              tenantEmail = tenantDoc.data().email || "";
              tenantName = tenantDoc.data().displayAs || "Tenant";
            }
          }
        }
      } catch (err) {
        console.warn("Could not fetch tenant email:", err);
      }

      if (!tenantEmail) {
        alert("Tenant email address not found. Please check lease details.");
        return;
      }

      this.loading = true;
      try {
        const emailData = {
          to: tenantEmail,
          cc: "rent@gqproperty.co.za",
          subject: `Invoice ${invoice.leaseReference} ${invoice.reference}`,
          text: `Dear ${tenantName},\n\nPlease find attached your invoice.\n\nThank you.`,
          pdfBase64: invoice.pdfBase64,
        };

        const response = await fetch("https://sendemail-bqr766ca3q-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(emailData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to resend invoice: ${errorText}`);
        }

        const result = await response.json();
        alert(result.message || "Invoice resent successfully!");

        // Update invoice status in Firestore
        const invoiceRef = doc(db, "Invoices", invoice.id);
        await updateDoc(invoiceRef, {
          status: "resent",
          resentAt: new Date().toISOString(),
        });

        // Refresh list to show updated status
        await this.fetchInvoices();
      } catch (error) {
        console.error("Error resending invoice:", error);
        alert("Failed to resend invoice. Please try again.");
      } finally {
        this.loading = false;
      }
    },
  },

  async created() {
    await this.fetchInvoices();
  },
};
</script>

<style scoped>
.invoice-history-page {
  padding: 20px;
}
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
}
.invoice-preview {
  width: 100%;
  min-height: 600px;
}
</style>